// ✅ 모바일 전용 RiskcheckEditor - 웹사이트 코드에 영향 없음
import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import MobileAttachmentBox from "../../common/papers/MobileAttachmentBox";

export default function MobileRiskcheckEditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const editorRef = useRef(null);
  const attachmentRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const safeUser =
    typeof currentUser === "string"
      ? (() => {
          try {
            return JSON.parse(currentUser);
          } catch {
            return {};
          }
        })()
      : currentUser || {};

  // -------------------------------
  // 1) 최상단 문서 정보
  // -------------------------------
  const [form, setForm] = useState({
    doc_date: initial.doc_date || today,
    status: initial.status || "none",
    title: initial.title || "",
    note: initial.note || "",
    author: initial.author || safeUser.name || "",
    created_at: initial.created_at || today,
  });

  // -------------------------------
  // 2) 평가 메타 정보
  // -------------------------------
  const [meta, setMeta] = useState({
    process: initial.process || "",
    location: initial.location || "",
    assessor: initial.assessor || "",
    reviewer: initial.reviewer || "",
    approver: initial.approver || "",
    risk_level: initial.risk_level || "",
    severity_score: initial.severity_score || "",
    likelihood_score: initial.likelihood_score || "",
    method: initial.method || "",
  });

  // 위험요소 / 개선계획
  const [hazards, setHazards] = useState(
    Array.isArray(initial.hazards) && initial.hazards.length > 0
      ? initial.hazards
      : [{ hazard: "", consequence: "", existing_measures: "" }]
  );

  const [improvements, setImprovements] = useState(
    Array.isArray(initial.risk_improvements) &&
      initial.risk_improvements.length > 0
      ? initial.risk_improvements
      : [{ risk_element: "", improve_plan: "", owner: "", improve_date: "" }]
  );

  const [showMeta, setShowMeta] = useState(false);
  const [showHazards, setShowHazards] = useState(true);
  const [showImprovements, setShowImprovements] = useState(true);

  const onChangeForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onChangeMeta = (k, v) => setMeta((p) => ({ ...p, [k]: v }));

  const autoTitle = useMemo(() => {
    const proc = meta.process?.trim();
    const loc = meta.location?.trim();
    if (proc && loc) return `[위험성평가] ${proc} - ${loc}`;
    if (proc) return `[위험성평가] ${proc}`;
    return "";
  }, [meta]);

  // -------------------------------
  // 부모로 데이터 전달
  // -------------------------------
  useEffect(() => {
    const editorNote =
      editorRef.current?.getContent?.({ format: "html" }) || form.note || "";

    onChange?.({
      dns: initial.dns || "",
      doc_type: "riskcheck",
      title: form.title?.trim() || autoTitle,
      status: form.status,
      meta: {
        ...meta,
        doc_date: form.doc_date,
        note: editorNote,
        hazards,
        risk_improvements: improvements,
      },
    });
  }, [form, meta, hazards, improvements, autoTitle, onChange, initial.dns]);

  // -------------------------------
  // 위험요소 조작 함수
  // -------------------------------
  const addHazard = () =>
    setHazards((p) => [
      ...p,
      { hazard: "", consequence: "", existing_measures: "" },
    ]);

  const rmHazard = (i) =>
    setHazards((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const upHazard = (i, k, v) =>
    setHazards((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  // -------------------------------
  // 개선계획 조작 함수
  // -------------------------------
  const addImp = () =>
    setImprovements((p) => [
      ...p,
      { risk_element: "", improve_plan: "", owner: "", improve_date: "" },
    ]);

  const rmImp = (i) => setImprovements((p) => p.filter((_, idx) => idx !== i));

  const upImp = (i, k, v) =>
    setImprovements((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r))
    );

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden">
      {/* =============================
          1) 문서 기본 정보 섹션
      ============================== */}
      <div className="space-y-3">
        {/* 제목 */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            제목
          </label>
          <input
            type="text"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={form.title}
            onChange={(e) => onChangeForm("title", e.target.value)}
            placeholder={autoTitle}
            disabled={readOnly}
          />
          <p className="text-xs text-gray-500">
            미입력 시 자동 제목: {autoTitle}
          </p>
        </div>

        {/* 평가일 + 문서작성일 */}
        <div className="grid grid-cols-1 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              평가일
            </label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.doc_date}
              onChange={(e) => onChangeForm("doc_date", e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              문서 작성일
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50"
              value={form.created_at || today}
              disabled
            />
          </div>
        </div>

        {/* 비고 */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            비고
          </label>
          <div className="min-w-0 w-full">
            <TinyMCEeditor
              initialValue={form.note || ""}
              editorRef={editorRef}
              onChange={(v) => onChangeForm("note", v)}
            />
          </div>
        </div>
      </div>

      {/* =============================
          2) 작업 및 평가 정보
      ============================== */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">
            {showMeta ? "작업 및 평가 정보" : "평가 세부사항"}
          </div>
          <button
            type="button"
            className="btn-basic-sm"
            onClick={() => setShowMeta((v) => !v)}
          >
            {showMeta ? "닫기" : "표로 입력하기"}
          </button>
        </div>

        {showMeta && (
          <div className="space-y-3 border border-gray-200 rounded-lg p-3 bg-white">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 block">
                공정
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={meta.process}
                onChange={(e) => onChangeMeta("process", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 block">
                작업 위치
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={meta.location}
                onChange={(e) => onChangeMeta("location", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 block">
                평가자
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={meta.assessor}
                onChange={(e) => onChangeMeta("assessor", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 block">
                평가 방법
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="JSA"
                value={meta.method}
                onChange={(e) => onChangeMeta("method", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 block">
                위험 수준
              </label>
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={meta.risk_level}
                onChange={(e) => onChangeMeta("risk_level", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 block">
                심각도 / 가능성
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  max={5}
                  className="w-24 border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={meta.severity_score}
                  onChange={(e) =>
                    onChangeMeta("severity_score", e.target.value)
                  }
                  disabled={readOnly}
                />
                <div className="relative flex-1">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 pr-6 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={meta.likelihood_score}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v < 1)
                        return onChangeMeta("likelihood_score", 1);
                      if (v > 100)
                        return onChangeMeta("likelihood_score", 100);
                      onChangeMeta("likelihood_score", v);
                    }}
                    disabled={readOnly}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =============================
          3) 위험 요소 테이블
      ============================== */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">위험 요소</div>
          <button
            type="button"
            className="btn-basic-sm"
            onClick={() => setShowHazards((v) => !v)}
          >
            {showHazards ? "닫기" : "표로 입력하기"}
          </button>
        </div>

        {showHazards && (
          <div className="space-y-3">
            {hazards.map((row, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-3 bg-white space-y-2"
              >
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    위험요소
                  </label>
                  {readOnly ? (
                    <div className="text-xs text-gray-900 break-words">
                      {row.hazard || "-"}
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={row.hazard}
                      onChange={(e) =>
                        upHazard(idx, "hazard", e.target.value)
                      }
                    />
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    예상결과
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={row.consequence}
                    onChange={(e) =>
                      upHazard(idx, "consequence", e.target.value)
                    }
                    disabled={readOnly}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    기존대책
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={row.existing_measures}
                    onChange={(e) =>
                      upHazard(idx, "existing_measures", e.target.value)
                    }
                    disabled={readOnly}
                  />
                </div>

                {!readOnly && (
                  <button
                    type="button"
                    className="btn-delete-sm w-full"
                    onClick={() => rmHazard(idx)}
                  >
                    삭제
                  </button>
                )}
              </div>
            ))}

            {!readOnly && (
              <button type="button" className="btn-basic-sm w-full" onClick={addHazard}>
                행 추가
              </button>
            )}
          </div>
        )}
      </div>

      {/* =============================
          4) 개선 계획 테이블
      ============================== */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">개선 계획</div>
          <button
            type="button"
            className="btn-basic-sm"
            onClick={() => setShowImprovements((v) => !v)}
          >
            {showImprovements ? "닫기" : "표로 입력하기"}
          </button>
        </div>

        {showImprovements && (
          <div className="space-y-3">
            {improvements.map((row, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-3 bg-white space-y-2"
              >
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    개선 대상
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={row.risk_element}
                    onChange={(e) =>
                      upImp(idx, "risk_element", e.target.value)
                    }
                    disabled={readOnly}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    개선 계획
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={row.improve_plan}
                    onChange={(e) =>
                      upImp(idx, "improve_plan", e.target.value)
                    }
                    disabled={readOnly}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    담당
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={row.owner}
                    onChange={(e) => upImp(idx, "owner", e.target.value)}
                    disabled={readOnly}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700 block">
                    완료일
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={row.improve_date}
                    onChange={(e) =>
                      upImp(idx, "improve_date", e.target.value)
                    }
                    disabled={readOnly}
                  />
                </div>

                {!readOnly && idx !== 0 && (
                  <button
                    type="button"
                    className="btn-delete-sm w-full"
                    onClick={() => rmImp(idx)}
                  >
                    삭제
                  </button>
                )}
              </div>
            ))}

            {!readOnly && (
              <button type="button" className="btn-basic-sm w-full" onClick={addImp}>
                행 추가
              </button>
            )}
          </div>
        )}
      </div>

      {/* 첨부파일 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          첨부파일
        </label>
        <MobileAttachmentBox ref={attachmentRef} />
      </div>
    </div>
  );
}

